import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';
import { debounce } from 'lodash';
import api from '../../services/api';
import MapPicker from './MapPicker';
import { Location, NewLocation, Address, Coordinates } from '../../types/location';

type LocationStatus = 'active' | 'inactive' | 'maintenance' | 'pending' | 'approved' | 'rejected';

interface MapLocation {
  coordinates: Coordinates;
  address?: Address;
}

interface Operator {
  _id: string;
  name: string;
  email: string;
  company: string;
}

// Add new interfaces for pagination and filters
interface PaginationState {
  page: number;
  limit: number;
  total: number;
}

interface LocationFilters {
  search: string;
  status: string;
  operator: string;
}

// Add interface for API response
interface LocationResponse {
  locations: Location[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const initialLocationState: NewLocation = {
  name: '',
  address: {
    street: '',
    ward: '',
    city: '',
    state: '',
    country: 'Vietnam',
    postalCode: ''
  },
  coordinates: {
    latitude: 0,
    longitude: 0
  },
  status: 'active',
  images: [],
  workingHours: {
    open: '00:00',
    close: '24:00'
  },
  contact: {
    website: '',
    phone: ''
  },
  amenities: {
    hasWaitingRoom: false,
    hasParking: false,
    hasRestroom: false,
    hasWifi: false,
    hasDrinks: false,
    hasFood: false,
    hasPark: false,
    hasRestaurant: false,
    hasShoppingMall: false
  },
  operatorId: ''
};

const INITIAL_PAGINATION: PaginationState = {
  page: 1,
  limit: 20,
  total: 0
};

const INITIAL_FILTERS: LocationFilters = {
  search: '',
  status: '',
  operator: ''
};

const LocationForm = React.memo(({ onLocationSelect, initialLocation, initialAddress }: { 
  onLocationSelect: (location: MapLocation) => void, 
  initialLocation?: Coordinates,
  initialAddress?: Address
}) => {
  return (
    <MapPicker
      onLocationSelect={onLocationSelect}
      initialLocation={initialLocation}
      initialAddress={initialAddress}
    />
  );
});

const uploadImage = async (file: File): Promise<string> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/settings/object-storage/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // The response will contain either the CDN URL or the full signed URL
    return response.data.url;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw new Error('Failed to upload image');
  }
};

// Improve the deleteImageFromStorage function for better error handling and logging
const deleteImageFromStorage = async (imageUrl: string): Promise<boolean> => {
  try {
    console.log('Attempting to delete image from storage:', imageUrl);
    
    // Extract the key from the URL
    let key = '';
    try {
      const url = new URL(imageUrl);
      const pathname = decodeURIComponent(url.pathname);
      
      // If URL contains s3goldtekvn, extract the path after it
      if (pathname.includes('s3goldtekvn/')) {
        key = pathname.split('s3goldtekvn/')[1];
      } else {
        // Otherwise just use the pathname without leading slash
        key = pathname.startsWith('/') ? pathname.substring(1) : pathname;
      }
      
      console.log('Extracted storage key:', key);
    } catch (error) {
      console.error('Error extracting key from URL:', error);
      return false;
    }
    
    if (!key) {
      console.error('Could not extract valid key from URL:', imageUrl);
      return false;
    }
    
    // Use encodeURIComponent to handle special characters in the key
    const encodedKey = encodeURIComponent(key);
    console.log('Sending delete request with key:', encodedKey);
    
    // Make the API call to delete the image
    const response = await api.delete(`/admin/object-storage/${encodedKey}`);
    console.log('Deletion API response:', response.data);
    
    return true;
  } catch (error) {
    console.error('Error deleting image from storage:', error);
    console.error('Full error details:', error);
    return false;
  }
};

const LocationList: React.FC = () => {
  const { t } = useTranslation();
  const [locations, setLocations] = useState<Location[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [newLocation, setNewLocation] = useState<NewLocation>(initialLocationState);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentOperatorId, setCurrentOperatorId] = useState<string>('');
  
  // Add new states for pagination and filtering
  const [pagination, setPagination] = useState<PaginationState>(INITIAL_PAGINATION);
  const [filters, setFilters] = useState<LocationFilters>(INITIAL_FILTERS);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Create a container ref for virtual scrolling
  const parentRef = React.useRef<HTMLDivElement>(null);

  // Set up virtual scrolling
  const rowVirtualizer = useVirtualizer({
    count: locations.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 60, // Estimated row height
    overscan: 5
  });

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      const newFilters = { 
        ...filters, 
        search: searchTerm,
        operator: !isAdmin ? currentOperatorId : filters.operator 
      };
      setFilters(newFilters);
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchLocations(1, pagination.limit, newFilters);
    }, 300),
    [filters, isAdmin, currentOperatorId]
  );

  useEffect(() => {
    const initializeComponent = async () => {
      try {
        // First get user data
        const userResponse = await api.get('/auth/me');
        console.log('User response:', userResponse.data);
        
        const isAdminUser = userResponse.data.role === 'admin';
        const operatorId = userResponse.data._id;
        
        console.log('User role:', userResponse.data.role);
        console.log('Operator ID:', operatorId);

        // Set initial filters based on user role
        const initialFilters = !isAdminUser ? {
          ...INITIAL_FILTERS,
          operator: operatorId
        } : INITIAL_FILTERS;

        // Set all states at once before fetching data
        setIsAdmin(isAdminUser);
        setCurrentOperatorId(operatorId);
        setFilters(initialFilters);

        // Wait for state updates
        await new Promise(resolve => setTimeout(resolve, 0));

        if (isAdminUser) {
          await fetchOperators();
        }

        // Make API call with direct values instead of state
        const response = await api.get<LocationResponse>('/locations', {
          params: {
            page: 1,
            limit: INITIAL_PAGINATION.limit,
            search: initialFilters.search,
            status: initialFilters.status,
            operator: !isAdminUser ? operatorId : initialFilters.operator
          }
        });

        console.log('Location response:', response.data);
        setLocations(response.data.locations || []);
        setPagination({
          page: response.data.page,
          limit: response.data.limit,
          total: response.data.total
        });
        
      } catch (error) {
        console.error('Error initializing component:', error);
        setError('Error loading data');
      } finally {
        setLoading(false);
      }
    };

    initializeComponent();
  }, []); // Run only once on component mount

  const fetchOperators = async () => {
    try {
      const response = await api.get('/operators');
      setOperators(response.data);
    } catch (error) {
      console.error('Error fetching operators:', error);
    }
  };

  // Add this function to properly handle status filtering
  const buildStatusQuery = (status: string) => {
    if (!status || status === '') {
      // When "All Statuses" is selected, show everything including pending
      return undefined; // No filter means show all statuses
    }
    return status;
  };

  // Memoized status filter handler
  const handleStatusFilter = useCallback((status: string) => {
    console.log('Handling status filter change:', status);
    console.log('Current operator ID:', currentOperatorId);
    console.log('Is admin:', isAdmin);

    const newFilters = {
      ...filters,
      status,
      // For non-admin users, always force their operator ID
      operator: !isAdmin ? currentOperatorId : filters.operator
    };

    console.log('New filters:', newFilters);
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));

    // Use the newFilters object directly in the fetchLocations call
    fetchLocations(1, pagination.limit, newFilters);
  }, [filters, isAdmin, currentOperatorId, pagination.limit]);

  // Modified fetchLocations to ensure filters are properly applied
  const fetchLocations = async (page: number, limit: number, currentFilters: LocationFilters) => {
    try {
      console.log('Fetching locations with filters:', currentFilters);
      setIsLoadingMore(page > 1);
      setLoading(page === 1);
      
      // Ensure we have the correct operator ID for non-admin users
      const finalFilters = {
        ...currentFilters,
        operator: !isAdmin ? currentOperatorId : currentFilters.operator,
        // Process status filter - undefined means show all
        status: buildStatusQuery(currentFilters.status)
      };

      console.log('Final filters for location fetch:', finalFilters);
      console.log('Using operator ID:', finalFilters.operator);
      console.log('Using status:', finalFilters.status);
      
      const response = await api.get<LocationResponse>('/locations', {
        params: {
          page,
          limit,
          search: finalFilters.search,
          status: finalFilters.status, // Will be undefined for "all" which shows everything
          operator: finalFilters.operator
        }
      });

      console.log('Location response:', response.data);

      if (page === 1) {
        setLocations(response.data.locations || []);
      } else if (response.data.locations && response.data.locations.length > 0) {
        setLocations(prev => [...prev, ...response.data.locations]);
      }

      setPagination({
        page: response.data.page,
        limit: response.data.limit,
        total: response.data.total
      });

    } catch (error: any) {
      console.error('Error fetching locations:', error);
      setError(error.response?.data?.error || 'Error fetching locations');
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Handle infinite scrolling
  const loadMoreItems = useCallback(() => {
    if (!isLoadingMore && locations.length < pagination.total) {
      const nextPage = pagination.page + 1;
      console.log(`Loading more items: page ${nextPage}, currently have ${locations.length} of ${pagination.total} total`);
      fetchLocations(nextPage, pagination.limit, filters);
    } else {
      console.log(`Not loading more items: isLoadingMore=${isLoadingMore}, loaded=${locations.length}, total=${pagination.total}`);
    }
  }, [pagination, filters, isLoadingMore, locations.length, fetchLocations]);

  // Handle scroll event to load more items
  const handleTableScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    // Load more when user scrolls to the bottom (with a buffer of 100px)
    if (scrollTop + clientHeight >= scrollHeight - 100 && !isLoadingMore && locations.length < pagination.total) {
      console.log('Scrolled to bottom, loading more items');
      loadMoreItems();
    }
  }, [isLoadingMore, loadMoreItems, locations.length, pagination.total]);

  // Remove the Intersection Observer since we're using scroll event
  useEffect(() => {
    console.log(`Current pagination: page ${pagination.page}, total ${pagination.total}, loaded ${locations.length}`);
  }, [pagination, locations.length]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Log the entire newLocation object
      console.log('Full newLocation object:', JSON.stringify(newLocation, null, 2));
      
      // Validate coordinates
      if (isNaN(newLocation.coordinates.latitude) || isNaN(newLocation.coordinates.longitude) ||
          (newLocation.coordinates.latitude === 0 && newLocation.coordinates.longitude === 0)) {
        setError('Please provide valid latitude and longitude coordinates');
        setLoading(false);
        return;
      }

      // Validate latitude range
      if (newLocation.coordinates.latitude < -90 || newLocation.coordinates.latitude > 90) {
        setError('Latitude must be between -90 and 90 degrees');
        setLoading(false);
        return;
      }

      // Validate longitude range
      if (newLocation.coordinates.longitude < -180 || newLocation.coordinates.longitude > 180) {
        setError('Longitude must be between -180 and 180 degrees');
        setLoading(false);
        return;
      }

      // Validate street address field
      if (!newLocation.address.street) {
        console.warn('Missing street address field');
        setError('Please provide a street address');
        setLoading(false);
        return;
      }

      console.log('Submitting location with address:', JSON.stringify(newLocation.address, null, 2));
      const locationData = {
        name: newLocation.name,
        address: {
          street: newLocation.address.street.trim(),
          ward: newLocation.address.ward?.trim() || '',
          city: newLocation.address.city?.trim() || '',
          state: newLocation.address.state?.trim() || '',
          country: newLocation.address.country || 'Vietnam',
          postalCode: newLocation.address.postalCode?.trim() || ''
        },
        coordinates: {
          latitude: newLocation.coordinates.latitude,
          longitude: newLocation.coordinates.longitude
        },
        status: newLocation.status,
        images: newLocation.images,
        workingHours: newLocation.workingHours,
        contact: newLocation.contact,
        amenities: newLocation.amenities,
        operatorId: newLocation.operatorId || undefined
      };

      console.log('Final location data being sent:', JSON.stringify(locationData, null, 2));

      let response;
      if (editingLocation) {
        console.log('Updating location:', editingLocation._id);
        response = await api.put(`/locations/${editingLocation._id}`, locationData);
      } else {
        console.log('Creating new location');
        response = await api.post('/locations', locationData);
      }
      
      console.log('Server response:', JSON.stringify(response.data, null, 2));

      // Reset form and refresh list
      setNewLocation(initialLocationState);
      setEditingLocation(null);
      fetchLocations(1, pagination.limit, filters);
    } catch (error: any) {
      console.error('Error saving location:', error);
      console.error('Error response:', error.response?.data);
      console.error('Full error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      setError(error.response?.data?.error || 'Error saving location');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('locations.confirmDelete'))) {
      return;
    }

    try {
      // First check if location is being used by any charge points
      const checkResponse = await api.get(`/locations/${id}/check-usage`);
      if (checkResponse.data.isUsed) {
        setError(t('locations.cannotDelete'));
        return;
      }

      await api.delete(`/locations/${id}`);
      fetchLocations(1, pagination.limit, filters);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Error deleting location');
    }
  };

  const handleStatusChange = useCallback((value: string) => {
    if (value === 'active' || value === 'inactive' || value === 'maintenance' || 
        value === 'pending' || value === 'approved' || value === 'rejected') {
      setNewLocation(prev => ({
        ...prev,
        status: value
      }));
    }
  }, []);

  const handleLocationSelect = useCallback((mapLocation: MapLocation) => {
    console.log('MapLocation received:', mapLocation);
    console.log('Current address:', mapLocation.address);
    
    setNewLocation(prev => {
      // Create the updated location object
      const updatedLocation = {
        ...prev,
        // Only update coordinates if they are valid (non-zero)
        coordinates: mapLocation.coordinates?.latitude && mapLocation.coordinates?.longitude ? {
          latitude: mapLocation.coordinates.latitude,
          longitude: mapLocation.coordinates.longitude
        } : prev.coordinates,
        // Only update address fields if they are provided by the MapPicker and not empty
        address: {
          street: mapLocation.address?.street || prev.address.street,
          ward: mapLocation.address?.ward ? mapLocation.address.ward : prev.address.ward,
          city: mapLocation.address?.city ? mapLocation.address.city : prev.address.city,
          state: mapLocation.address?.state ? mapLocation.address.state : prev.address.state,
          country: mapLocation.address?.country ? mapLocation.address.country : prev.address.country,
          postalCode: mapLocation.address?.postalCode || prev.address.postalCode
        }
      };

      // Log the final updated location for debugging
      console.log('Updated location:', updatedLocation);
      return updatedLocation;
    });
  }, []);

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setNewLocation(prev => ({
      ...prev,
      name: e.target.value
    }));
  }, []);

  const handleOperatorChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOperatorId = e.target.value;
    console.log('Selected operator ID:', selectedOperatorId);
    setNewLocation(prev => ({
      ...prev,
      operatorId: selectedOperatorId
    }));
  }, []);

  const handleApproveLocation = async (id: string) => {
    if (!window.confirm(t('locations.confirmApprove'))) {
      return;
    }

    try {
      await api.patch(`/locations/public/${id}/status`, { status: 'approved' });
      fetchLocations(1, pagination.limit, filters);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Error approving location');
    }
  };

  const handleRejectLocation = async (id: string) => {
    if (!window.confirm(t('locations.confirmReject'))) {
      return;
    }

    try {
      await api.patch(`/locations/public/${id}/status`, { status: 'rejected' });
      fetchLocations(1, pagination.limit, filters);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Error rejecting location');
    }
  };

  // Update renderFilters to include new status options
  const renderFilters = () => (
    <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
      <div>
        <input
          type="text"
          placeholder={t('locations.search')}
          className="w-full px-3 py-2 border rounded"
          onChange={(e) => debouncedSearch(e.target.value)}
        />
      </div>
      <div>
        <select
          className="w-full px-3 py-2 border rounded"
          value={filters.status}
          onChange={(e) => handleStatusFilter(e.target.value)}
        >
          <option value="">{t('locations.allStatuses')}</option>
          <option value="active">{t('locations.locationStatus.active')}</option>
          <option value="inactive">{t('locations.locationStatus.inactive')}</option>
          <option value="maintenance">{t('locations.locationStatus.maintenance')}</option>
          <option value="pending">{t('locations.locationStatus.pending')}</option>
          <option value="approved">{t('locations.locationStatus.approved')}</option>
          <option value="rejected">{t('locations.locationStatus.rejected')}</option>
        </select>
      </div>
      {isAdmin && (
        <div>
          <select
            className="w-full px-3 py-2 border rounded"
            value={filters.operator}
            onChange={(e) => {
              const newFilters = { ...filters, operator: e.target.value };
              setFilters(newFilters);
              setPagination(prev => ({ ...prev, page: 1 }));
              fetchLocations(1, pagination.limit, newFilters);
            }}
          >
            <option value="">{t('locations.allOperators')}</option>
            {operators.map(operator => (
              <option key={operator._id} value={operator._id}>
                {operator.name} ({operator.company})
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );

  // Render location count summary
  const renderLocationCount = () => {
    const selectedOperator = operators.find(op => op._id === filters.operator);
    
    return (
      <div className="mb-4 text-gray-600 font-medium">
        {filters.search ? (
          <p>
            {t(
              'locations.searchResults', 
              `Found ${pagination.total} locations matching "${filters.search}"`,
              { count: pagination.total, term: filters.search }
            )}
          </p>
        ) : filters.operator ? (
          <p>
            {t(
              'locations.operatorLocations', 
              `Showing ${locations.length} of ${pagination.total} locations for ${selectedOperator?.name || 'selected operator'}`,
              { 
                showing: locations.length,
                total: pagination.total,
                operator: selectedOperator?.name || filters.operator 
              }
            )}
          </p>
        ) : filters.status ? (
          <p>
            {t(
              'locations.statusLocations', 
              `Showing ${locations.length} of ${pagination.total} ${filters.status} locations`,
              { 
                showing: locations.length,
                total: pagination.total, 
                status: t(`locations.locationStatus.${filters.status}`, filters.status)
              }
            )}
          </p>
        ) : (
          <p>
            {t(
              'locations.totalLocations', 
              `Showing ${locations.length} of ${pagination.total} total locations`,
              { showing: locations.length, total: pagination.total }
            )}
          </p>
        )}
      </div>
    );
  };

  // Modify the table rendering to use virtual scrolling
  const renderTable = () => (
    <div 
      ref={parentRef} 
      className="h-[600px] overflow-auto" 
      onScroll={handleTableScroll}
    >
      <table className="min-w-full bg-white">
        <thead className="sticky top-0 bg-white shadow z-10">
          <tr>
            <th className="px-4 py-2">{t('locations.name')}</th>
            {isAdmin && <th className="px-4 py-2">{t('locations.operator')}</th>}
            <th className="px-4 py-2">{t('locations.address')}</th>
            <th className="px-4 py-2">{t('locations.workingHours.title')}</th>
            <th className="px-4 py-2">{t('locations.status')}</th>
            <th className="px-4 py-2">{t('common.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {locations.map((location, index) => (
            <tr key={location._id} className="hover:bg-gray-50">
              <td className="border px-4 py-2">{location.name}</td>
              {isAdmin && (
                <td className="border px-4 py-2">
                  {location.operatorId?.name || 'Unknown'}
                  <br />
                  <span className="text-sm text-gray-500">
                    {location.operatorId?.company}
                  </span>
                </td>
              )}
              <td className="border px-4 py-2">
                {[
                  location.address?.street,
                  location.address?.ward,
                  location.address?.city,
                  location.address?.state,
                  location.address?.country,
                  location.address?.postalCode && `(${location.address.postalCode})`
                ]
                  .filter(Boolean)
                  .join(', ')}
              </td>
              <td className="border px-4 py-2">
                {location.workingHours?.open} - {location.workingHours?.close}
              </td>
              <td className="border px-4 py-2">
                <span className={`px-2 py-1 rounded ${
                  location.status === 'active' ? 'bg-green-100 text-green-800' :
                  location.status === 'inactive' ? 'bg-red-100 text-red-800' :
                  location.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                  location.status === 'pending' ? 'bg-blue-100 text-blue-800' :
                  location.status === 'approved' ? 'bg-green-100 text-green-800' :
                  location.status === 'rejected' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {t(`locations.locationStatus.${location.status}`)}
                </span>
              </td>
              <td className="border px-4 py-2">
                {location.status === 'pending' && isAdmin && (
                  <>
                    <button
                      onClick={() => handleApproveLocation(location._id)}
                      className="text-green-600 hover:text-green-800 mr-2"
                      title={t('locations.approve')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleRejectLocation(location._id)}
                      className="text-red-600 hover:text-red-800 mr-2"
                      title={t('locations.reject')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </>
                )}
                <button
                  onClick={() => {
                    setEditingLocation(location);
                    setNewLocation({
                      name: location.name,
                      address: location.address,
                      coordinates: location.coordinates,
                      status: location.status,
                      images: location.images || [],
                      workingHours: location.workingHours || { open: '00:00', close: '24:00' },
                      contact: location.contact || { website: '', phone: '' },
                      amenities: location.amenities || {
                        hasWaitingRoom: false,
                        hasParking: false,
                        hasRestroom: false,
                        hasWifi: false,
                        hasDrinks: false,
                        hasFood: false
                      },
                      operatorId: location.operatorId?._id || ''
                    });
                    setShowForm(true);
                  }}
                  className="text-blue-600 hover:text-blue-800 mr-2"
                >
                  {t('common.edit')}
                </button>
                <button
                  onClick={() => handleDelete(location._id)}
                  className="text-red-600 hover:text-red-800"
                >
                  {t('common.delete')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Loading trigger element at the bottom */}
      <div id="loading-trigger" className="text-center py-4">
        {isLoadingMore ? (
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mr-2"></div>
            {t('common.loading')}
          </div>
        ) : (
          locations.length < pagination.total && (
            <div>
              <div className="mb-2 text-gray-600">
                {t(
                  'locations.showingCount', 
                  `Showing ${locations.length} of ${pagination.total} locations`,
                  { showing: locations.length, total: pagination.total }
                )}
              </div>
              <button 
                onClick={loadMoreItems}
                className="px-4 py-2 bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
              >
                {t('common.loadMore')} ({locations.length} / {pagination.total})
              </button>
            </div>
          )
        )}
      </div>
    </div>
  );

  if (loading && locations.length === 0) {
    return <div className="flex justify-center items-center h-64">{t('common.loading')}</div>;
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">{t('locations.title')}</h1>
        {!showForm && (
          <button
            onClick={() => {
              setShowForm(true);
              setEditingLocation(null);
              setNewLocation(initialLocationState);
            }}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            {t('locations.addLocation')}
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {showForm ? (
        <>
          <h2 className="text-2xl font-bold mb-4">
            {editingLocation ? t('locations.editLocation') : t('locations.addLocation')}
          </h2>
          <form onSubmit={async (e) => {
            e.preventDefault();
            try {
              await handleSubmit(e);
              setShowForm(false);
            } catch (error) {
              // Error is already handled in handleSubmit
            }
          }} className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Operator Field - Only show for admin */}
              {isAdmin && (
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    {t('locations.operator')}
                  </label>
                  <select
                    value={newLocation.operatorId}
                    onChange={handleOperatorChange}
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  >
                    <option value="">{t('common.select')}</option>
                    {operators.map((operator) => (
                      <option key={operator._id} value={operator._id}>
                        {operator.name} ({operator.company})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Name Field */}
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t('locations.locationName')}
                </label>
                <input
                  type="text"
                  value={newLocation.name}
                  onChange={handleNameChange}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  required
                />
              </div>

              {/* Map Picker Section */}
              <div className="col-span-2 mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t('locations.mapLocation')} <span className="text-gray-400">({t('locations.selectOrManualEdit')})</span>
                </label>
                <LocationForm
                  onLocationSelect={handleLocationSelect}
                  initialLocation={
                    newLocation.coordinates.latitude !== 0 
                      ? newLocation.coordinates 
                      : undefined
                  }
                  initialAddress={editingLocation ? {
                    street: newLocation.address.street,
                    ward: newLocation.address.ward,
                    city: newLocation.address.city,
                    state: newLocation.address.state,
                    country: newLocation.address.country,
                    postalCode: newLocation.address.postalCode
                  } : undefined}
                />
              </div>

              {/* Address Fields - Make them readonly since they're populated by MapPicker */}
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t('locations.streetAddress')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newLocation.address.street}
                  onChange={(e) => setNewLocation({
                    ...newLocation,
                    address: {
                      ...newLocation.address,
                      street: e.target.value
                    }
                  })}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t('locations.ward')} <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={newLocation.address.ward}
                  onChange={(e) => setNewLocation({
                    ...newLocation,
                    address: {
                      ...newLocation.address,
                      ward: e.target.value
                    }
                  })}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t('locations.district')} <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={newLocation.address.city}
                  onChange={(e) => setNewLocation({
                    ...newLocation,
                    address: {
                      ...newLocation.address,
                      city: e.target.value
                    }
                  })}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t('locations.stateProvince')} <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={newLocation.address.state}
                  onChange={(e) => setNewLocation({
                    ...newLocation,
                    address: {
                      ...newLocation.address,
                      state: e.target.value
                    }
                  })}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t('locations.country')}
                </label>
                <input
                  type="text"
                  value={newLocation.address.country}
                  onChange={(e) => setNewLocation({
                    ...newLocation,
                    address: {
                      ...newLocation.address,
                      country: e.target.value
                    }
                  })}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t('locations.postalCode')}
                </label>
                <input
                  type="text"
                  value={newLocation.address.postalCode || ''}
                  onChange={(e) => setNewLocation({
                    ...newLocation,
                    address: {
                      ...newLocation.address,
                      postalCode: e.target.value
                    }
                  })}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>

              {/* Coordinates - Make them readonly since they're populated by MapPicker */}
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t('locations.latitude')}
                </label>
                <input
                  type="text"
                  value={newLocation.coordinates.latitude}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow valid number input
                    if (value === '' || value === '-' || /^-?\d*\.?\d*$/.test(value)) {
                      setNewLocation({
                        ...newLocation,
                        coordinates: {
                          ...newLocation.coordinates,
                          latitude: value === '' ? 0 : parseFloat(value)
                        }
                      });
                    }
                  }}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="e.g. 10.762622"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t('locations.longitude')}
                </label>
                <input
                  type="text"
                  value={newLocation.coordinates.longitude}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow valid number input
                    if (value === '' || value === '-' || /^-?\d*\.?\d*$/.test(value)) {
                      setNewLocation({
                        ...newLocation,
                        coordinates: {
                          ...newLocation.coordinates,
                          longitude: value === '' ? 0 : parseFloat(value)
                        }
                      });
                    }
                  }}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="e.g. 106.660172"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t('locations.status')}
                </label>
                <select
                  value={newLocation.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  required
                >
                  <option value="active">{t('locations.locationStatus.active')}</option>
                  <option value="inactive">{t('locations.locationStatus.inactive')}</option>
                  <option value="maintenance">{t('locations.locationStatus.maintenance')}</option>
                  <option value="pending">{t('locations.locationStatus.pending')}</option>
                  <option value="approved">{t('locations.locationStatus.approved')}</option>
                  <option value="rejected">{t('locations.locationStatus.rejected')}</option>
                </select>
              </div>

              {/* Working Hours */}
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t('locations.workingHours.openingTime')}
                </label>
                <input
                  type="time"
                  value={newLocation.workingHours?.open || '00:00'}
                  onChange={(e) => setNewLocation({
                    ...newLocation,
                    workingHours: {
                      ...newLocation.workingHours,
                      open: e.target.value
                    }
                  })}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t('locations.workingHours.closingTime')}
                </label>
                <input
                  type="time"
                  value={newLocation.workingHours?.close || '24:00'}
                  onChange={(e) => setNewLocation({
                    ...newLocation,
                    workingHours: {
                      ...newLocation.workingHours,
                      close: e.target.value
                    }
                  })}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>

              {/* Contact Information */}
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t('locations.contact.phone')}
                </label>
                <input
                  type="tel"
                  value={newLocation.contact?.phone || ''}
                  onChange={(e) => setNewLocation({
                    ...newLocation,
                    contact: {
                      ...newLocation.contact,
                      phone: e.target.value
                    }
                  })}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t('locations.contact.website')}
                </label>
                <input
                  type="url"
                  value={newLocation.contact?.website || ''}
                  onChange={(e) => setNewLocation({
                    ...newLocation,
                    contact: {
                      ...newLocation.contact,
                      website: e.target.value
                    }
                  })}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="https://"
                />
              </div>

              {/* Images */}
              <div className="col-span-2">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t('locations.images.title')} ({t('locations.images.maxLimit')})
                </label>
                <div className="space-y-4">
                  {/* File Upload */}
                  <div className="flex items-center space-x-2">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={async (e) => {
                        if (!e.target.files) return;
                        
                        try {
                          const uploadPromises = Array.from(e.target.files)
                            .slice(0, 6 - (newLocation.images?.length || 0))
                            .map(file => uploadImage(file));

                          const uploadedUrls = await Promise.all(uploadPromises);
                          
                          setNewLocation(prev => ({
                            ...prev,
                            images: [...(prev.images || []), ...uploadedUrls]
                          }));
                        } catch (error) {
                          setError('Error uploading images');
                        }
                      }}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-full file:border-0
                        file:text-sm file:font-semibold
                        file:bg-blue-50 file:text-blue-700
                        hover:file:bg-blue-100"
                    />
                  </div>

                  {/* Image URLs */}
                  <div>
                    <label className="block text-gray-700 text-sm mb-2">
                      {t('locations.images.addUrls')}
                    </label>
                    <textarea
                      value={newLocation.images?.join('\n') || ''}
                      onChange={(e) => {
                        const urls = e.target.value.split('\n').filter(url => url.trim()).slice(0, 6);
                        setNewLocation(prev => ({
                          ...prev,
                          images: urls
                        }));
                      }}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      rows={3}
                      placeholder={t('locations.images.addUrls')}
                    />
                  </div>

                  {/* Preview Images */}
                  {newLocation.images && newLocation.images.length > 0 && (
                    <div className="grid grid-cols-3 gap-4">
                      {newLocation.images.map((image, index) => (
                        <div key={index} className="relative group">
                          <div className="w-full h-32 bg-gray-100 rounded flex items-center justify-center overflow-hidden">
                            <img
                              src={image}
                              alt={`${t('locations.images.preview')} ${index + 1}`}
                              className="w-full h-auto max-h-32 object-contain rounded"
                              onError={async (e) => {
                                const img = e.target as HTMLImageElement;
                                // Only try to get a signed URL if the image URL doesn't start with the CDN URL
                                // and doesn't already have a signature
                                if (!img.src.includes('?signature') && !img.src.includes('?X-Amz-')) {
                                  try {
                                    // Get a signed URL for the image
                                    const response = await api.get(`/settings/object-storage/signed-url/${encodeURIComponent(image)}`);
                                    img.src = response.data.url;
                                  } catch (error) {
                                    console.error('Error getting signed URL:', error);
                                    img.src = '/placeholder-image.jpg'; // Fallback image
                                  }
                                } else {
                                  img.src = '/placeholder-image.jpg'; // Fallback image for CDN failures
                                }
                              }}
                            />
                          </div>
                          <button
                            onClick={async (e) => {
                              // Prevent event propagation to avoid closing the form
                              e.preventDefault();
                              e.stopPropagation();
                              
                              // Ask for confirmation before deletion
                              if (!window.confirm(t('locations.images.confirmRemove') || 'Are you sure you want to remove this image?')) {
                                return;
                              }
                              
                              // First delete the image from storage
                              try {
                                const deleted = await deleteImageFromStorage(image);
                                if (deleted) {
                                  console.log('Image successfully deleted from storage');
                                } else {
                                  console.warn('Failed to delete image from storage, but will remove from UI');
                                }
                              } catch (error) {
                                console.error('Error during image deletion:', error);
                              }
                              
                              // Then remove the image from the location form
                              const newImages = newLocation.images?.filter((_, i) => i !== index) || [];
                              setNewLocation(prev => ({
                                ...prev,
                                images: newImages
                              }));
                            }}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1
                              opacity-0 group-hover:opacity-100 transition-opacity"
                            title={t('locations.images.remove') || 'Remove image'}
                            type="button" // Explicitly set to button to avoid form submission
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Amenities */}
              <div className="col-span-2">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t('locations.amenities.title')}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      name="hasWaitingRoom"
                      checked={newLocation.amenities?.hasWaitingRoom || false}
                      onChange={(e) => setNewLocation({
                        ...newLocation,
                        amenities: {
                          ...newLocation.amenities,
                          [e.target.name]: e.target.checked
                        }
                      })}
                      className="form-checkbox h-5 w-5 text-blue-600"
                    />
                    <span className="ml-2">{t('locations.amenities.waitingRoom')}</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      name="hasParking"
                      checked={newLocation.amenities?.hasParking || false}
                      onChange={(e) => setNewLocation({
                        ...newLocation,
                        amenities: {
                          ...newLocation.amenities,
                          [e.target.name]: e.target.checked
                        }
                      })}
                      className="form-checkbox h-5 w-5 text-blue-600"
                    />
                    <span className="ml-2">{t('locations.amenities.parking')}</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      name="hasRestroom"
                      checked={newLocation.amenities?.hasRestroom || false}
                      onChange={(e) => setNewLocation({
                        ...newLocation,
                        amenities: {
                          ...newLocation.amenities,
                          [e.target.name]: e.target.checked
                        }
                      })}
                      className="form-checkbox h-5 w-5 text-blue-600"
                    />
                    <span className="ml-2">{t('locations.amenities.restroom')}</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      name="hasWifi"
                      checked={newLocation.amenities?.hasWifi || false}
                      onChange={(e) => setNewLocation({
                        ...newLocation,
                        amenities: {
                          ...newLocation.amenities,
                          [e.target.name]: e.target.checked
                        }
                      })}
                      className="form-checkbox h-5 w-5 text-blue-600"
                    />
                    <span className="ml-2">{t('locations.amenities.wifi')}</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      name="hasDrinks"
                      checked={newLocation.amenities?.hasDrinks || false}
                      onChange={(e) => setNewLocation({
                        ...newLocation,
                        amenities: {
                          ...newLocation.amenities,
                          [e.target.name]: e.target.checked
                        }
                      })}
                      className="form-checkbox h-5 w-5 text-blue-600"
                    />
                    <span className="ml-2">{t('locations.amenities.drinks')}</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      name="hasFood"
                      checked={newLocation.amenities?.hasFood || false}
                      onChange={(e) => setNewLocation({
                        ...newLocation,
                        amenities: {
                          ...newLocation.amenities,
                          [e.target.name]: e.target.checked
                        }
                      })}
                      className="form-checkbox h-5 w-5 text-blue-600"
                    />
                    <span className="ml-2">{t('locations.amenities.food')}</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      name="hasPark"
                      checked={newLocation.amenities?.hasPark || false}
                      onChange={(e) => setNewLocation({
                        ...newLocation,
                        amenities: {
                          ...newLocation.amenities,
                          [e.target.name]: e.target.checked
                        }
                      })}
                      className="form-checkbox h-5 w-5 text-blue-600"
                    />
                    <span className="ml-2">{t('locations.amenities.park')}</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      name="hasRestaurant"
                      checked={newLocation.amenities?.hasRestaurant || false}
                      onChange={(e) => setNewLocation({
                        ...newLocation,
                        amenities: {
                          ...newLocation.amenities,
                          [e.target.name]: e.target.checked
                        }
                      })}
                      className="form-checkbox h-5 w-5 text-blue-600"
                    />
                    <span className="ml-2">{t('locations.amenities.restaurant')}</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      name="hasShoppingMall"
                      checked={newLocation.amenities?.hasShoppingMall || false}
                      onChange={(e) => setNewLocation({
                        ...newLocation,
                        amenities: {
                          ...newLocation.amenities,
                          [e.target.name]: e.target.checked
                        }
                      })}
                      className="form-checkbox h-5 w-5 text-blue-600"
                    />
                    <span className="ml-2">{t('locations.amenities.shoppingMall')}</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Submit and Cancel Buttons */}
            <div className="flex justify-end space-x-2 mt-4">
              <button
                type="submit"
                disabled={loading}
                className={`bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ${
                  loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? t('common.loading') : (editingLocation ? t('common.save') : t('locations.addLocation'))}
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingLocation(null);
                }}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </>
      ) : (
        <div>
          {renderFilters()}
          {renderLocationCount()}
          {renderTable()}
        </div>
      )}
    </div>
  );
};

export default LocationList; 